import { commonWebSocket } from '@services/common/common.constants';
import { createReducer } from 'redux-act';
import { IEcoAgreement, IEcoPurchase, IEcoTransfer, IEcoPendingChange, IEcoSplitSetting } from '@app-types/websocket/economy.types';

const defaultState:{agreements:IEcoAgreement[], purchases:IEcoPurchase[],
  transfers:IEcoTransfer[], splitSetting:IEcoSplitSetting[],

  } = {
  agreements : [],
  purchases:[],
  transfers:[],
  splitSetting:[],
};

const reducer = createReducer<typeof defaultState>({}, defaultState);

// The server can serialize optional fields as null, which slips past the
// `!== undefined` guards in the economy components and crashes the render
// (e.g. null.length). Normalize everything here so the rest of the app can
// rely on the declared types.
const normalizeArray = (items: any): any[] => {
  return Array.isArray(items)
    ? items.filter((item) => item !== undefined && item !== null)
    : [];
};

const normalizeOptionalString = (value: any): string|undefined => {
  return typeof value === "string" ? value : undefined;
};

const normalizeTitle = (value: any): string => {
  return typeof value === "string" ? value : "";
};

const toFiniteNumber = (value: any): number|undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

// Server sends amounts in hundredths; the app uses whole units.
const normalizeOptionalAmount = (value: any): number|undefined => {
  const num = toFiniteNumber(value);
  return num !== undefined ? num / 100 : undefined;
};

const normalizeRequiredAmount = (value: any): number => {
  const num = toFiniteNumber(value);
  return num !== undefined ? num / 100 : 0;
};

const normalizePendingChange = (pendingChange: any): IEcoPendingChange|undefined => {
  if (!pendingChange || typeof pendingChange !== "object") {
    return undefined;
  }

  return {
    ...pendingChange,
    responses: normalizeArray(pendingChange.responses),
    split: normalizeArray(pendingChange.split),
    archivedBy: normalizeArray(pendingChange.archivedBy),
    amount: normalizeOptionalAmount(pendingChange.amount),
  };
};

const normalizeAgreement = (agreement: any): IEcoAgreement => {
  return {
    ...agreement,
    title: normalizeTitle(agreement.title),
    description: normalizeOptionalString(agreement.description),
    responses: normalizeArray(agreement.responses),
    children: normalizeArray(agreement.children),
    images: normalizeArray(agreement.images),
    responsible: normalizeArray(agreement.responsible),
    split: normalizeArray(agreement.split),
    archivedBy: normalizeArray(agreement.archivedBy),
    seenBy: normalizeArray(agreement.seenBy),
    amount: normalizeOptionalAmount(agreement.amount),
    pendingChange: normalizePendingChange(agreement.pendingChange),
  };
};

const normalizePurchase = (purchase: any): IEcoPurchase => {
  return {
    ...purchase,
    title: normalizeTitle(purchase.title),
    description: normalizeOptionalString(purchase.description),
    images: normalizeArray(purchase.images),
    split: normalizeArray(purchase.split),
    children: normalizeArray(purchase.children),
    seenBy: normalizeArray(purchase.seenBy),
    archivedBy: normalizeArray(purchase.archivedBy),
    amount: normalizeRequiredAmount(purchase.amount),
  };
};

const normalizeTransfer = (transfer: any): IEcoTransfer => {
  return {
    ...transfer,
    note: normalizeOptionalString(transfer.note),
    seenBy: normalizeArray(transfer.seenBy),
    archivedBy: normalizeArray(transfer.archivedBy),
    sum: normalizeRequiredAmount(transfer.sum),
  };
};

reducer
  .on(commonWebSocket.special.ECO_AGREEMENT_UPDATE, (state: any, payload: any) => {
    if (!Array.isArray(payload?.ecoAgreements)) {
      return state;
    }

    let oldAgreementList:IEcoAgreement[] = Array.isArray(state.agreements) ? state.agreements.slice() : [];
    const newAgreementList:IEcoAgreement[] = [];
    for (const agreementUpdate of payload.ecoAgreements) {
      if (!agreementUpdate || agreementUpdate.id === undefined) {
        continue;
      }
      const agreement = normalizeAgreement(agreementUpdate);
      const index = oldAgreementList.findIndex((ag) => ag.id === agreement.id);
      if (index > -1) {
        oldAgreementList[index] = agreement;
      } else {
        newAgreementList.push(agreement);
      }
    }

    oldAgreementList = oldAgreementList.concat(newAgreementList);

    return {
      ...state,
      agreements:oldAgreementList,
    };
  })
  .on(commonWebSocket.special.ECO_PURCHASE_UPDATE, (state: any, payload: any) => {
    if (!Array.isArray(payload?.ecoPurchases)) {
      return state;
    }

    let oldPurchaseList:IEcoPurchase[] = Array.isArray(state.purchases) ? state.purchases.slice() : [];
    const newPurchasesList:IEcoPurchase[] = [];
    for (const purchaseUpdate of payload.ecoPurchases) {
      if (!purchaseUpdate || purchaseUpdate.id === undefined) {
        continue;
      }
      const purchase = normalizePurchase(purchaseUpdate);
      const index = oldPurchaseList.findIndex((ag) => ag.id === purchase.id);
      if (index > -1) {
        oldPurchaseList[index] = purchase;
      } else {
        newPurchasesList.push(purchase);
      }
    }

    oldPurchaseList = oldPurchaseList.concat(newPurchasesList);

    return {
      ...state,
      purchases:oldPurchaseList,
    };
  })
  .on(commonWebSocket.special.ECO_TRANSFER_UPDATE, (state: any, payload: any) => {
    if (!Array.isArray(payload?.ecoTransfers)) {
      return state;
    }

    let oldTransfersList:IEcoTransfer[] = Array.isArray(state.transfers) ? state.transfers.slice() : [];
    const newTransfersList:IEcoTransfer[] = [];
    for (const transferUpdate of payload.ecoTransfers) {
      if (!transferUpdate || transferUpdate.id === undefined) {
        continue;
      }
      const transfer = normalizeTransfer(transferUpdate);
      const index = oldTransfersList.findIndex((ag) => ag.id === transfer.id);
      if (index > -1) {
        oldTransfersList[index] = transfer;
      } else {
        newTransfersList.push(transfer);
      }
    }

    oldTransfersList = oldTransfersList.concat(newTransfersList);

    return {
      ...state,
      transfers:oldTransfersList,
    };
  });

export default reducer;



// WEBPACK FOOTER //
// ./src/services/economy/economy.reducer.ts